import * as Ariakit from '@ariakit/react';
import { useQuery, type QueryKey, type UseQueryOptions } from '@tanstack/react-query';
import cn from 'classnames';
import { useEffect, type ComponentProps } from 'react';
import { SelectButton } from '#components/atoms/buttons/select-button/SelectButton.tsx';
import { HelpTip } from '#components/atoms/forms/help-tip/HelpTip.tsx';
import { ErrorMessage } from '#components/atoms/forms/index.ts';
import { Field } from '#components/patterns/forms/field/Field.tsx';
import { SelectItemLoading } from '#components/patterns/forms/select/select-item-loading/SelectItemLoading.tsx';
import { SelectItem } from '#components/patterns/forms/select/select-item/SelectItem.tsx';
import { SelectProvider } from '#components/patterns/forms/select/SelectContext.tsx';
import { SelectMenu } from '../select-menu/SelectMenu.tsx';
import type { Select } from '../select/Select.tsx';

export type Item = {
  value: string;
  label: React.ReactNode;
  hint?: string;
};

const DEFAULT_PLACEHOLDER = 'Please select…';

type Props<T extends string, TQueryData, TQueryError extends Error, TQueryKey extends QueryKey> = {
  children: (items: Item[]) => React.ReactNode;
  /**
   * A react-query queryOptions to be passed to {@link useQuery},
   * with data being returned as an array of `items`.
   *
   * The query is made immediately upon mount, not lazily upon dropdown open.
   */
  getItemsQueryOptions: UseQueryOptions<TQueryData, TQueryError, { items: Item[] }, TQueryKey>;
  modifyItems?: (asyncItems: Item[]) => Item[];
  /** Plural name of options being shown, used in various status messages. */
  optionsName?: string;
  /** The item with a matching `value` property will be the default selected item, if found */
  defaultValue?: string | null;
  /** an onChange e.g. from react-hook-form */
  onChange?: (value: string) => void;
  size?: 'xsmall' | 'small' | 'medium';
  portal?: ComponentProps<typeof SelectMenu>['portal'];
  /** To better match next to secondary buttons */
  variant?: 'secondary';
  /** An icon to show in the select button */
  icon?: React.ComponentProps<typeof SelectButton>['icon'];
  /** A className to apply to the SelectMenu component */
  menuClassName?: string;
} & Omit<React.ComponentProps<typeof Select<T>>, 'children'>;

export function SelectAsync<T extends string, TQueryData, TQueryError extends Error, TQueryKey extends QueryKey>({
  innerRef,
  ...props
}: Props<T, TQueryData, TQueryError, TQueryKey>) {
  const {
    optionsName = 'options',
    placeholder = DEFAULT_PLACEHOLDER,
    // Workaround to https://github.com/ariakit/ariakit/issues/2516
    defaultValue = '',
    modifyItems,
    size,
    variant,
    icon,
    menuClassName,
  } = props;
  const select = Ariakit.useSelectStore({ defaultValue, ...props.storeProps });
  const isOpen = Ariakit.useStoreState(select, 'open');
  const value = Ariakit.useStoreState(select, 'value') as T; // Can't figure out any better way right now
  const { setState: setSelectState } = select;

  const gotItems = useQuery(props.getItemsQueryOptions);
  const { isLoading, error } = gotItems;
  const items = modifyItems?.(gotItems.data?.items ?? []) ?? gotItems.data?.items ?? [];

  // Call onChange whenever value changes, to pick up on changes due to key presses.
  // It will also fire a dupe when an option is clicked, but that seems fine.
  const { onChange } = props;
  useEffect(() => {
    if (onChange) {
      onChange(value);
    }
  }, [value, onChange]);

  useEffect(() => {
    if (props.value !== undefined) {
      setSelectState('value', props.value);
    }
  }, [props.value, setSelectState]);

  let menu: React.ReactElement | React.ReactNode | null = null;
  if (isLoading) {
    menu = <SelectItemLoading>Loading {optionsName}…</SelectItemLoading>;
  } else if (error instanceof Error) {
    menu = (
      <SelectItem isMessage={true} className="center">
        <ErrorMessage>{error.message}</ErrorMessage>
      </SelectItem>
    );
  } else if (items.length) {
    menu = props.children(items);
  } else {
    menu = (
      <SelectItem isMessage={true} className="center">
        No {optionsName} found
      </SelectItem>
    );
  }

  function findItemByValue(needleVal: string) {
    return items.find((itm) => itm.value === needleVal);
  }

  return (
    <SelectProvider store={select}>
      <Field
        className={props.className}
        label={
          <div className={cn('label flex align-baseline', { 'visually-hidden': props.visuallyHideLabel })}>
            <Ariakit.SelectLabel store={select}>{props.label}</Ariakit.SelectLabel>
            {props.helpTip ?
              <HelpTip>{props.helpTip}</HelpTip>
            : null}
          </div>
        }
      >
        {props.help}
        <div>
          <Ariakit.Select
            store={select}
            className="select"
            ref={innerRef}
            disabled={(value.length > 0 && isLoading) || props.disabled}
            onBlur={props.onBlur}
            render={
              <SelectButton
                variant={variant}
                icon={icon}
                hasValue={Boolean(findItemByValue(value)?.label)}
                hasError={props.hasError ?? Boolean(props.errorMessage)}
                isMenuOpen={isOpen}
                size={size}
              />
            }
          >
            {value.length > 0 && isLoading ? 'Loading...' : (findItemByValue(value)?.label ?? placeholder)}
          </Ariakit.Select>
          <SelectMenu type="select" store={select} portal={props.portal} className={menuClassName}>
            {menu}
          </SelectMenu>
        </div>
        {props.errorMessage && <ErrorMessage>{props.errorMessage}</ErrorMessage>}
        {props.fieldChildren}
      </Field>
    </SelectProvider>
  );
}
